import {
  AHText,
  AHTextFonts,
  Box,
  TouchableBox,
  useScaleFactor,
  useWidgetOptions,
} from "arkh";
import { DateTime } from "luxon";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FlatList, View } from "react-native";
import { useWidgetLayout } from "../WidgetLayoutContext";
import { graphql, useFragment } from "react-relay";
import { useSetWidgetFocus } from "../useSetFocus";
import { Employee } from "./WidgetEmployeeScheduleShifts";

import { useInterval } from "../../useInterval";

import { WidgetEmployeeSchedule_widgetEmployeeSchedule$key } from "./__generated__/WidgetEmployeeSchedule_widgetEmployeeSchedule.graphql";
import { totalHours } from "./totalHours";
import { WidgetEmployeeScheduleIcon } from "./WidgetEmployeeScheduleIcon/WidgetEmployeeScheduleIcon";
import { TimeBackgroundLines } from "./TimeBackgroundLines";
import { WidgetEvent } from "../WidgetEventT";

export const WidgetEmployeeSchedule = (props: {
  widget: WidgetEmployeeSchedule_widgetEmployeeSchedule$key;
  storecastId?: string;
  mode?: "FOCUS" | "IDLE";

  onEvent?: (event: WidgetEvent) => void;
  interactive?: undefined | false;
}) => {
  const widget = useFragment(
    graphql`
      fragment WidgetEmployeeSchedule_widgetEmployeeSchedule on WidgetEmployeeSchedule {
        id
        overviewSize
        teams {
          id
        }
        ...WidgetEmployeeScheduleIcon_widgetEmployeeSchedule
        employeesWithShifts {
          id
          shifts {
            status
          }

          ...WidgetEmployeeScheduleShiftsEmployee_employee
        }
      }
    `,
    props.widget
  );
  const setFocus = useSetWidgetFocus(widget.id, props.storecastId);
  const layout = useWidgetLayout();
  const width = layout.width;

  const employeePaneWidth = Math.floor(width / 6);
  const boxWidth = (width - employeePaneWidth) / (totalHours * 2);

  const sf = useScaleFactor();

  const doubleListHeight = Math.floor(35 * sf);
  const singleListHeight = Math.floor(20 * sf);
  const isDoubleHeight =
    (widget.employeesWithShifts.length - 1) * doubleListHeight <
    layout.height - (doubleListHeight + 10 * sf);

  const listItemHeight = Math.floor(
    isDoubleHeight ? doubleListHeight : singleListHeight
  );

  const headerHeight = listItemHeight + 10 * sf;

  const startHour = 5;

  const startMins = startHour * 60;
  const startD = useMemo(
    () =>
      DateTime.local().set({
        hour: 5,
        minute: 0,
        second: 0,
        millisecond: 0,
      }),
    []
  );

  const firstNotEndedEmployee = useMemo(
    () =>
      Math.max(
        0,
        widget.employeesWithShifts.findIndex((employee) => {
          return employee.shifts.some((shift) => {
            return shift.status !== "PAST";
          });
        }) - 1
      ),
    [widget.employeesWithShifts]
  );

  const scrollRef = React.useRef<FlatList>(null);

  const scrollToNow = useCallback(() => {
    if (
      firstNotEndedEmployee !== -1 &&
      widget.employeesWithShifts.length > 0 &&
      props.interactive !== false
    ) {
      scrollRef.current?.scrollToIndex({
        index: firstNotEndedEmployee,
        animated: true,
      });
    }
  }, [
    firstNotEndedEmployee,
    props.interactive,
    widget.employeesWithShifts.length,
  ]);

  useEffect(() => {
    scrollToNow();
  }, [
    firstNotEndedEmployee,
    listItemHeight,
    props.interactive,
    scrollToNow,
    widget.overviewSize,
  ]);

  const [mode, setMode] = useState<"TEAM" | "TIME">("TEAM");
  useInterval(
    () => {
      setMode((mode) => (mode === "TEAM" ? "TIME" : "TEAM"));
    },
    props.interactive === false ? null : 3500
  );

  const getItemLayout = useCallback(
    (_: unknown, index: number) => ({
      length: listItemHeight,
      offset: listItemHeight * index,
      index,
    }),
    [listItemHeight]
  );

  type Item = (typeof widget.employeesWithShifts)[number];
  const bottomInset = useMemo(() => ({ bottom: headerHeight }), [headerHeight]);
  const renderEmployee = useCallback(
    ({ item }: { item: Item }) => (
      <Employee
        key={item.id}
        employee={item}
        boxWidth={boxWidth}
        isDoubleHeight={isDoubleHeight}
        showTime={mode === "TIME"}
        dayStartMinutes={startMins}
        employeePaneWidth={employeePaneWidth}
        listItemHeight={listItemHeight}
      />
    ),
    [
      boxWidth,
      employeePaneWidth,
      isDoubleHeight,
      listItemHeight,
      mode,
      startMins,
    ]
  );

  const { onEvent } = props;
  const { background } = useWidgetOptions();
  const onPress = useCallback(() => {
    onEvent?.({
      kind: "WidgetEmployeeSchedule",
      teamIds: widget.teams.map((team) => team.id),
    });
  }, [onEvent, widget.teams]);

  return (
    <TouchableBox
      onPress={props.mode === "FOCUS" ? () => setFocus() : onPress}
      color="surface"
      style={{ flex: 1, backgroundColor: background }}
    >
      <WidgetEmployeeScheduleIcon width={employeePaneWidth} widget={widget} />
      <TimeHeader
        headerHeight={headerHeight}
        boxWidth={boxWidth}
        employeePaneWidth={employeePaneWidth}
      />
      <TimeBackgroundLines
        headerHeight={headerHeight}
        left={employeePaneWidth}
        boxWidth={boxWidth}
        startD={startD}
      />
      {props.interactive === false ? (
        <View>
          {widget.employeesWithShifts.slice(0, 10).map((item) =>
            renderEmployee({
              item,
            })
          )}
        </View>
      ) : (
        <FlatList
          ref={scrollRef}
          getItemLayout={getItemLayout}
          automaticallyAdjustContentInsets={false}
          showsVerticalScrollIndicator={false}
          data={widget.employeesWithShifts}
          renderItem={renderEmployee}
          contentInset={bottomInset}
        />
      )}
    </TouchableBox>
  );
};

const TimeHeader = React.memo(TimeHeader_);
function TimeHeader_(props: {
  headerHeight: number;
  employeePaneWidth: number;
  boxWidth: number;
}) {
  const sf = useScaleFactor();
  const { background } = useWidgetOptions();
  return (
    <Box
      color="surface"
      style={{
        flexDirection: "row",
        height: props.headerHeight,
        left: props.employeePaneWidth,
        backgroundColor: background,
      }}
    >
      {new Array(totalHours - 1).fill(0).map((_, i) => {
        return (
          <AHText
            key={i + ""}
            style={{
              width: props.boxWidth * 2,
              flex: 0,
              left: i * props.boxWidth * 2 + props.boxWidth,
              textAlign: "center",
              top: 8 * sf,
              fontSize: Math.round(11 * sf),
              position: "absolute",
            }}
            numberOfLines={1}
            font={AHTextFonts.BOLDER}
            color="darkerText"
            type="detail"
          >
            {("00" + Math.floor(i + 6)).slice(-2)}
          </AHText>
        );
      })}
    </Box>
  );
}
